"use client";

import {
  type BaseQueryApi,
  type BaseQueryFn,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query";

import { publicConfig } from "@/configs/public-config";
import { ErrorCode } from "@/providers/error-handler/enums/error-codes.enum";
import { shouldSetServerError } from "@/providers/error-handler/should-set-server-error";
import { ENavigationKeys } from "@/providers/navigation/enums/navigation-keys.enum";
import { refreshAccessToken, refreshMutex } from "@/providers/tokens/refresh-access-token";
import { TokenService } from "@/providers/tokens/tokens.service";
import { type IApiResponseError } from "@/shared/interfaces/api-response.interface";

import {
  setServerError, setClientError, toggleLoading, setForbiddenError,
} from "../slices/app/slice";
import { logout } from "../slices/user/slice";

const glAPI = fetchBaseQuery({
  baseUrl: `${publicConfig.apiUrl}/${publicConfig.apiTag}`,
  prepareHeaders(headers: Headers) {
    const accessToken = TokenService.getAccessToken();
    if (accessToken) {
      headers.set(publicConfig.authHeader, `Bearer ${accessToken}`);
    }
    return headers;
  },
});

export interface ICustomFetchBaseExtraOptions {
  /** Let the caller handle 5xx itself (e.g. toast) instead of redirecting to the global error page */
  skipServerErrorRedirect?: boolean;
}

const handleError = (
  statusCode: number | undefined,
  api: BaseQueryApi,
  route: string,
  data: Pick<IApiResponseError, "message">,
  options: ICustomFetchBaseExtraOptions,
) => {
  if (!statusCode) return;

  const code = statusCode as ErrorCode;

  if (code === ErrorCode.FORBIDDEN) {
    api.dispatch(setForbiddenError(true));
    return;
  }

  const isAuthRoute = route.includes(ENavigationKeys.Auth);

  if ((isAuthRoute && code === ErrorCode.UNAUTHORIZED)
    || code === ErrorCode.BAD_REQUEST
    || code === ErrorCode.UNPROCESSABLE_ENTITY
    || code === ErrorCode.CONFLICT) {
    api.dispatch(setClientError({ key: route, data: { ...data, code: statusCode } }));
  } else if (code === ErrorCode.UNAUTHORIZED) {
    // TODO tmp solution because of issues with 401 handling but must be 400 for account settings routes
    if (route.includes(ENavigationKeys.AccountSettings)) {
      api.dispatch(setClientError({ key: route, data: { ...data, code: statusCode } }));
      return;
    }

    api.dispatch(logout());
  } else if (shouldSetServerError(code) && !options.skipServerErrorRedirect) {
    api.dispatch(setServerError(true));
  }
};

const processError = (
  result: { error?: FetchBaseQueryError },
  api: BaseQueryApi,
  route: string,
  options: ICustomFetchBaseExtraOptions,
) => {
  const statusCode = result.error?.status as number | undefined;
  const errorData = (result.error?.data as IApiResponseError);
  const message = errorData?.message || "";
  handleError(statusCode, api, route, { message }, options);
};

const customFetchBase: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  ICustomFetchBaseExtraOptions
> = async (
  args,
  api,
  extraOptions,
) => {
  api.dispatch(toggleLoading(true));
  const route = window.location.pathname;

  await refreshMutex.waitForUnlock();
  const sentAccessToken = TokenService.getAccessToken();
  let result = await glAPI(args, api, extraOptions);

  const refreshToken = TokenService.getRefreshToken();
  const isUnauthorized = result.error?.status === ErrorCode.UNAUTHORIZED;

  if (isUnauthorized && refreshToken) {
    const refreshResult = await refreshAccessToken(sentAccessToken);

    if (refreshResult === "refreshed") {
      result = await glAPI(args, api, extraOptions);
      processError(result, api, route, extraOptions ?? {});
    } else if (refreshResult === "unauthorized") {
      processError(result, api, route, extraOptions ?? {});
    }
    // "failed" (429 / 5xx / network): keep the session and just fail this request
  } else {
    processError(result, api, route, extraOptions ?? {});
  }

  api.dispatch(toggleLoading(false));
  return result;
};

export default customFetchBase;
